import { useState, useEffect } from 'react';
import { AlertTriangle, LogOut, Smartphone, Tablet, Laptop } from 'lucide-react';
import usePotvrzeni from '../ui/usePotvrzeni';

/**
 * Přihlášená zařízení — moje relace a odhlášení cizího telefonu.
 *
 * Osobní věc, takže patří každému (revize Nastavení, 2026-10-12): dřív to
 * byla skupina „Osobní" ve starém Nastavení, kam ale chodili jen admini.
 * Otevírá se z profilu (desktop: menu profilu v liště, mobil: Můj profil).
 */
export default function PrihlasenaZarizeni({ isDark }) {
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revoking, setRevoking] = useState(null); // session id
    const [revokingAll, setRevokingAll] = useState(false);

    const mutedColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.72)' : 'var(--text-secondary)';
    const borderColor = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';

    const load = () => {
        setLoading(true);
        fetch('/auth/sessions')
            .then(r => r.ok ? r.json() : [])
            .then(setSessions)
            .catch(() => setSessions([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const csrfToken = () => decodeURIComponent(
        document.cookie.split('; ').find(r => r.startsWith('XSRF-TOKEN='))?.split('=')[1] || ''
    );

    const revokeOne = async (id, label) => {
        if (!(await potvrdit(`Odhlásit zařízení „${label}“?`, { potvrdit: 'Odhlásit' }))) return;
        setRevoking(id);
        await fetch(`/auth/sessions/${id}`, {
            method: 'DELETE',
            headers: { 'Accept': 'application/json', 'X-XSRF-TOKEN': csrfToken() },
        });
        setRevoking(null);
        load();
    };

    const revokeAll = async () => {
        const others = sessions.filter(s => !s.is_current).length;
        if (others === 0) return;
        if (!(await potvrdit(`Odhlásit ${others} ostatních zařízení?`, { potvrdit: 'Odhlásit vše' }))) return;
        setRevokingAll(true);
        await fetch('/auth/sessions', {
            method: 'DELETE',
            headers: { 'Accept': 'application/json', 'X-XSRF-TOKEN': csrfToken() },
        });
        setRevokingAll(false);
        load();
    };

    const formatAgo = (iso) => {
        const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
        if (min < 2) return 'právě teď';
        if (min < 60) return `před ${min} min`;
        if (min < 1440) return `před ${Math.round(min / 60)} h`;
        return `před ${Math.round(min / 1440)} d`;
    };

    const formatExpires = (iso) => {
        const days = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 86400000));
        return days > 0 ? `vyprší za ${days} d` : 'vyprší dnes';
    };

    const otherCount = sessions.filter(s => !s.is_current).length;

    return (
        <div>
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Aktivní zařízení</h3>
                    <p className="text-xs" style={{ color: mutedColor }}>
                        Zde vidíš všechna zařízení, kde jsi aktuálně přihlášen. Kterékoli z nich můžeš na dálku odhlásit.
                    </p>
                </div>
                {otherCount > 0 && (
                    <button
                        onClick={revokeAll}
                        disabled={revokingAll}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap"
                        style={{
                            background: 'rgba(239,68,68,0.10)',
                            color: '#EF4444',
                            border: '1px solid rgba(239,68,68,0.25)',
                            cursor: revokingAll ? 'not-allowed' : 'pointer',
                            opacity: revokingAll ? 0.5 : 1,
                        }}
                    >
                        <LogOut size={14} /> {revokingAll ? 'Odhlašuji…' : `Odhlásit ostatní (${otherCount})`}
                    </button>
                )}
            </div>

            {loading && (
                <div className="text-sm py-8 text-center" style={{ color: mutedColor }}>Načítám…</div>
            )}

            {!loading && sessions.length === 0 && (
                <div className="text-sm py-8 text-center" style={{ color: mutedColor }}>Žádná aktivní zařízení.</div>
            )}

            {!loading && sessions.length > 0 && (
                <div className="space-y-2">
                    {sessions.map((s) => {
                        const DevIcon = s.device?.device === 'mobile' ? Smartphone
                            : s.device?.device === 'tablet' ? Tablet : Laptop;
                        const label = `${s.device?.browser || 'Neznámý'}${s.device?.os ? ' · ' + s.device.os : ''}`;
                        const isCurrent = !!s.is_current;
                        return (
                            <div key={s.id}
                                className="flex items-center gap-3 px-3 py-3 rounded-xl"
                                style={{
                                    background: isCurrent
                                        ? (isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.05)')
                                        : (isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)'),
                                    border: `1px solid ${isCurrent ? 'rgba(34,197,94,0.25)' : borderColor}`,
                                }}>
                                <div style={{
                                    width: 38, height: 38, borderRadius: 7, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isCurrent ? 'rgba(34,197,94,0.12)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                                    color: isCurrent ? '#22C55E' : (isDark ? 'rgba(255,255,255,0.6)' : secondaryColor),
                                }}>
                                    <DevIcon size={18} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)', margin: 0 }}>
                                            {label}
                                        </p>
                                        {isCurrent && (
                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider"
                                                style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>
                                                Toto zařízení
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs" style={{ color: mutedColor, margin: '2px 0 0' }}>
                                        {s.ip_address || '—'} · {formatAgo(s.last_activity)} · {formatExpires(s.expires_at)}
                                    </p>
                                </div>
                                {!isCurrent && (
                                    <button
                                        onClick={() => revokeOne(s.id, label)}
                                        disabled={revoking === s.id}
                                        title="Odhlásit toto zařízení"
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium shrink-0"
                                        style={{
                                            background: 'transparent',
                                            color: '#EF4444',
                                            border: '1px solid rgba(239,68,68,0.30)',
                                            cursor: revoking === s.id ? 'not-allowed' : 'pointer',
                                            opacity: revoking === s.id ? 0.5 : 1,
                                        }}>
                                        <LogOut size={12} /> {revoking === s.id ? '…' : 'Odhlásit'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-6 p-3 rounded-xl flex items-start gap-2.5"
                style={{
                    background: isDark ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.04)',
                    border: '1px solid rgba(59,130,246,0.20)',
                }}>
                <AlertTriangle size={14} style={{ color: '#3B82F6', flexShrink: 0, marginTop: 2 }} />
                <p className="text-xs" style={{ color: secondaryColor, margin: 0 }}>
                    Brzy přidáme možnost přihlášení otiskem prstu / Windows Hello / Face ID. Důvěryhodná zařízení pak budou trvale spárovaná a každé bude mít svůj vlastní záznam.
                </p>
            </div>
            {potvrzeni}
        </div>
    );
}
